import { useLocation } from 'react-router-dom';
import { useEffect, useMemo, useRef, useState } from 'react';
import logoSymbole from '@/assets/logo-luminjo-symbole.png';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useUnsavedChangesSafe } from '@/contexts/UnsavedChangesContext';
import { useUnreadTickets } from '@/hooks/useUnreadTickets';
import { useWorkspacePresence, isDndActive } from '@/hooks/useWorkspacePresence';
import { useProfile } from '@/hooks/useProfile';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { mainNavItems, OnlineIndicatorIcon, MoonIcon } from '@/config/icons';

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

export function AppSidebar() {
  const location = useLocation();
  const { activeWorkspace } = useWorkspaceContext();
  const { profile } = useProfile();
  const { handleNavigation } = useUnsavedChangesSafe();
  const unreadTickets = useUnreadTickets();
  const prevUnreadRef = useRef(unreadTickets);
  const [isPulsing, setIsPulsing] = useState(false);
  
  // Trigger pulse animation when unread count increases
  useEffect(() => {
    if (unreadTickets > prevUnreadRef.current) {
      setIsPulsing(true);
      const timer = setTimeout(() => setIsPulsing(false), 2000);
      return () => clearTimeout(timer);
    }
    prevUnreadRef.current = unreadTickets;
  }, [unreadTickets]);
  
  const currentUser = useMemo(
    () =>
      profile
        ? {
            id: profile.id,
            firstName: profile.first_name,
            lastName: profile.last_name,
            avatarUrl: profile.avatar_url,
            dndEnabled: profile.dnd_enabled,
            dndUntil: profile.dnd_until,
          }
        : null,
    [
      profile?.id,
      profile?.first_name,
      profile?.last_name,
      profile?.avatar_url,
      profile?.dnd_enabled,
      profile?.dnd_until,
    ],
  );

  const { connectedAgents, totalCount } = useWorkspacePresence({
    workspaceId: activeWorkspace?.id,
    currentUser,
  });

  const isActive = (path: string) => location.pathname === path;

  const logoLink = activeWorkspace ? `/space/${activeWorkspace.id}` : '/';

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>, url: string) => {
    e.preventDefault();
    handleNavigation(url);
  };

  return (
    <Sidebar
      className="w-32 border-r border-sidebar-border sticky top-0 h-screen"
      collapsible="none"
    >
      <SidebarHeader className="py-5 flex items-center justify-center bg-sidebar">
        <a 
          href={logoLink} 
          onClick={(e) => handleLinkClick(e, logoLink)}
          className="block hover:opacity-90 transition-opacity"
        >
          <img 
            src={logoSymbole} 
            alt="Luminjo" 
            className="h-12 w-auto"
            width={48}
            height={48}
            fetchPriority="high"
          />
        </a>
      </SidebarHeader>

      <SidebarContent className="px-2 py-2 bg-sidebar">
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu className="flex flex-col gap-1">
              {mainNavItems.map((item) => {
                const active = isActive(item.url);
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={active}
                      tooltip={item.title}
                      className="h-auto p-0"
                    >
                      <a 
                        href={item.url}
                        onClick={(e) => handleLinkClick(e, item.url)}
                        className={cn(
                          "flex flex-col items-center justify-center gap-2 py-3 px-2 rounded-xl transition-all duration-200",
                          active 
                            ? "bg-sidebar-accent" 
                            : "hover:bg-sidebar-accent/50"
                        )}
                      >
                        <div className="relative">
                          <item.icon 
                            className="h-5 w-5 text-sidebar-foreground" 
                            strokeWidth={1.5} 
                          />
                          {item.hasBadge && unreadTickets > 0 && (
                            <span 
                              className={cn(
                                "absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground",
                                isPulsing && "animate-[pulse_0.5s_ease-in-out_3]"
                              )}
                              role="status"
                              aria-label={`${unreadTickets} ticket${unreadTickets > 1 ? 's' : ''} non lu${unreadTickets > 1 ? 's' : ''}`}
                            >
                              <span aria-hidden="true">{unreadTickets > 9 ? '9+' : unreadTickets}</span>
                            </span>
                          )}
                        </div>
                        <span className={cn(
                          "text-[11px] text-center leading-tight text-sidebar-foreground",
                          active ? "font-semibold" : "font-medium"
                        )}>
                          {item.multiline ? (
                            <>Réponses<br />Types</>
                          ) : (
                            item.title
                          )}
                        </span>
                      </a>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Connected agents counter */}
      <SidebarFooter className="p-3 bg-sidebar border-t border-sidebar-border">
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex flex-col items-center gap-2 cursor-default">
              <div className="flex -space-x-2">
                {connectedAgents.slice(0, 3).map((agent) => {
                  const isAgentDnd = isDndActive(agent.dnd_enabled, agent.dnd_until);
                  return (
                    <div key={agent.id} className="relative">
                      <Avatar className="h-6 w-6 border-2 border-sidebar">
                        <AvatarImage src={agent.avatar_url || undefined} alt={agent.name} />
                        <AvatarFallback className="text-[10px] bg-primary text-primary-foreground">
                          {agent.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {isAgentDnd && (
                        <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-warning border border-sidebar flex items-center justify-center">
                          <MoonIcon className="h-2 w-2 text-warning-foreground" />
                        </span>
                      )}
                    </div>
                  );
                })}
                {totalCount > 3 && (
                  <div className="h-6 w-6 rounded-full bg-muted border-2 border-sidebar flex items-center justify-center">
                    <span className="text-[10px] font-medium text-muted-foreground">+{totalCount - 3}</span>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1.5" role="status" aria-label={`${totalCount} agent${totalCount > 1 ? 's' : ''} en ligne`}>
                <OnlineIndicatorIcon className="h-2 w-2 fill-luminjo-success text-luminjo-success" aria-hidden="true" />
                <span className="text-[11px] text-sidebar-foreground font-medium" aria-hidden="true">
                  {totalCount} en ligne
                </span>
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-[200px]">
            <div className="space-y-1">
              <p className="font-medium text-sm">Agents connectés</p>
              {connectedAgents.map((agent) => {
                const isAgentDnd = isDndActive(agent.dnd_enabled, agent.dnd_until);
                return (
                  <div key={agent.id} className="flex items-center gap-2 text-sm">
                    {isAgentDnd ? (
                      <MoonIcon className="h-2.5 w-2.5 text-warning" aria-hidden="true" />
                    ) : (
                      <OnlineIndicatorIcon className="h-1.5 w-1.5 fill-luminjo-success text-luminjo-success" aria-hidden="true" />
                    )}
                    <span>{agent.name}</span>
                    {isAgentDnd && (
                      <span className="text-xs text-warning">(Ne pas déranger)</span>
                    )}
                    <span className="sr-only">{isAgentDnd ? '(ne pas déranger)' : '(en ligne)'}</span>
                  </div>
                );
              })}
            </div>
          </TooltipContent>
        </Tooltip>
      </SidebarFooter>
    </Sidebar>
  );
}
